import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaHistory, FaDownload, FaPrint, FaCalculator, FaYoutube, FaInstagram } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { campaignService } from '../../services/companyAuthService';

import { printComponent } from '../../../utils/printUtils';
import PrintablePaymentDetails from './PrintablePaymentDetails';
import { exportToCSV } from "../../../utils/exportUtils";

const AffiliatePaymentDetails = () => {
  const { campaignId, affiliateId } = useParams();
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState({
    totalDue: 0,
    amountPaid: 0,
    amountUnpaid: 0
  });
  const [affiliateInfo, setAffiliateInfo] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [paymentHistory, setPaymentHistory] = useState([]);

  const [calculationData, setCalculationData] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [showCalculationDetails, setShowCalculationDetails] = useState(false);
  const [showRevenueDetails, setShowRevenueDetails] = useState(false);
  const [autoCalculating, setAutoCalculating] = useState(false);
  const [hasAutoCalculated, setHasAutoCalculated] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');


  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (campaignId && affiliateId) {
        try {
          const response = await campaignService.getAffiliatePaymentDetails(campaignId, affiliateId);
          if (response.success) {
            // Set payment data from existing record
            setPaymentData(response.data.paymentDetails);
            
            // Set affiliate info
            if (response.data.affiliate) {
              setAffiliateInfo(response.data.affiliate);
            }
            
            // Set payment history
            if (response.data.paymentHistory) {
              setPaymentHistory(response.data.paymentHistory);
            }

            // Set calculation data if available (for display purposes)
            if (response.data.calculationData) {
              setCalculationData(response.data.calculationData);
              setShowCalculationDetails(true);
            }

            // Update payment data with the latest totalDue if it changed
            setPaymentData({
              totalDue: response.data.paymentDetails.totalDue,
              amountPaid: response.data.paymentDetails.amountPaid || 0,
              amountUnpaid: response.data.paymentDetails.totalDue -(response.data.paymentDetails.amountPaid || 0),
            });
            
          }
        } catch (error) {
          console.log('No existing payment record found, will auto-calculate');
          // This is expected for new affiliates, auto-calculation will handle it
        } finally {
          setInitialLoading(false);
        }
      } else {
        setInitialLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [campaignId, affiliateId]);

  const handlePaymentAmountChange = (e) => {
    const value = e.target.value;
    const numValue = value === '' ? '' : parseFloat(value.replace(/^0+/, ''));
    setPaymentAmount(numValue);
  };

  const handlePayNow = () => {
    setShowPaymentForm(true);
  };

  const handleClosePaymentForm = () => {
    setShowPaymentForm(false);
    setPaymentAmount('');
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!paymentAmount || paymentAmount <= 0) {
      toast.error('Payment amount must be greater than 0');
      return;
    }
    
    if (paymentAmount > paymentData.amountUnpaid) {
      toast.error('Payment amount cannot exceed remaining due amount');
      return;
    }
    
    setLoading(true);
    try {
      const newAmountPaid = (paymentData.amountPaid || 0) + paymentAmount;
      const response = await campaignService.updateAffiliatePaymentDetails(campaignId, affiliateId, {
        totalDue: paymentData.totalDue,
        amountPaid: newAmountPaid
      });

      if (response.success) {
        // Update payment history with new payment
        setPaymentHistory(prev => [response.data.latestHistory, ...prev]);

        // Update current payment data
        setPaymentData(response.data.paymentDetails);

        // Close payment form and reset
        setShowPaymentForm(false);
        setPaymentAmount('');

        toast.success('Payment processed successfully');
      } else {
        toast.error(response.message || 'Failed to process payment');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to process payment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const headers = [
      { label: 'Date', accessor: row => row.updatedAt, isDate: true },
      { label: 'Total Due', accessor: row => row.totalDue, isCurrency: true },
      { label: 'Amount Paid', accessor: row => row.amountPaid, isCurrency: true },
      { label: 'Amount Unpaid', accessor: row => row.amountUnpaid, isCurrency: true },
      { label: 'Updated By', accessor: row => row.lastUpdatedBy?.name || row.brandName || 'System' },
      { label: 'Status', accessor: row => row.amountUnpaid === 0 ? 'Paid' : row.amountPaid > 0 ? 'Partial' : 'Unpaid' }
    ];

    // Add affiliate info to the payment history
    const enrichedHistory = paymentHistory.map(payment => ({
      ...payment,
      affiliateName: `${affiliateInfo.firstName} ${affiliateInfo.lastName}`,
      affiliateEmail: affiliateInfo.email
    }));

    exportToCSV(enrichedHistory, 'payment_details.csv', headers);
    toast.success('Exporting payment history...');
  };

  const handlePrint = () => {
    const printProps = {
      paymentData: paymentData,
      affiliateInfo: affiliateInfo,
      paymentHistory: paymentHistory
    };
    printComponent(PrintablePaymentDetails, printProps);
  };

  const performCalculation = useCallback(async (isAutoCalculation = false) => {
    try {
      const response = await campaignService.calculateAffiliatePayment(campaignId, affiliateId);
      
      if (response.success) {
        setCalculationData(response.data);
        
        // Set affiliate info from the calculation response
        if (response.data.affiliate) {
          const nameParts = response.data.affiliate.name?.split(' ') || [];
          setAffiliateInfo({
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
            email: response.data.affiliate.email || '',
            username: response.data.affiliate.username || '',
            instagramId: response.data.affiliate.instagramId || '',
            affiliateType: response.data.affiliate.affiliateType || '',
            companyName: response.data.affiliate.companyName || ''
          });
        }
        
        // Auto-fill the total due amount (includes new revenue + previous unpaid amounts)
        const calculatedAmount = response.data.total.revenue; // New revenue only
        const currentAmountPaid = paymentData.amountPaid || 0;
        
        // Use totalDue from backend which includes previous unpaid amounts
        const totalDueAmount = response.data.incrementalInfo?.totalDue || calculatedAmount;
        
        setPaymentData(prev => ({
          ...prev,
          totalDue: totalDueAmount,
          amountUnpaid: totalDueAmount - currentAmountPaid
        }));
        
        if (!isAutoCalculation) {
          if (totalDueAmount > 0) {
            const message = response.data.incrementalInfo?.isIncremental 
              ? `Payment recalculated successfully! New revenue: ₹${calculatedAmount.toFixed(2)}, Total remaining due: ₹${totalDueAmount.toFixed(2)}`
              : `Payment calculated successfully! Total due: ₹${totalDueAmount.toFixed(2)}`;
            toast.success(message);
          } else if (response.data.incrementalInfo?.isIncremental) {
            toast.info('No new revenue since last calculation. Total due remains ₹' + (currentAmountPaid || 0).toFixed(2) + ' (unpaid amount).');
          } else {
            toast.info('Payment calculated but revenue is zero. Check campaign configuration and approved media.');
          }
        }
        setShowCalculationDetails(true);
      } else {
        if (!isAutoCalculation) {
          toast.error(response.message || 'Failed to calculate payment');
        }
        throw new Error(response.message || 'Calculation failed');
      }
    } catch (error) {
      console.error('Error calculating payment:', error);
      if (!isAutoCalculation) {
        if (error.message?.includes('No approved media')) {
          toast.warning('No approved media found for this affiliate. They need to submit and get approval for their content first.');
        } else if (error.message?.includes('Campaign not found')) {
          toast.error('Campaign not found or you do not have permission to access it.');
        } else {
          toast.error('Failed to calculate payment. Please try again.');
        }
      }
      throw error;
    }
  },[campaignId, affiliateId, paymentData.amountPaid]);

  useEffect(() => {
    const autoCalculatePayments = async () => {
      if (
        !initialLoading &&
        !hasAutoCalculated &&
        campaignId &&
        affiliateId &&
        !calculationData
      ) {
        setAutoCalculating(true);
        try {
          await new Promise(res => setTimeout(res, 800));
          await performCalculation(true);
          setHasAutoCalculated(true);
        } finally {
          setAutoCalculating(false);
        }
      } else if (calculationData) {
        setHasAutoCalculated(true);
      }
    };
  
    autoCalculatePayments();
  }, [
    initialLoading,
    hasAutoCalculated,
    campaignId,
    affiliateId,
    calculationData,
    performCalculation,
  ]);
  
  const calculatePayment = async () => {
    if (!campaignId || !affiliateId) {
      toast.error('Missing campaign or affiliate information');
      return;
    }
    
    setCalculating(true);
    try {
      await performCalculation(false);
    } catch (error) {
      console.error('Manual calculation failed:', error);
      // Error handling is already done in performCalculation
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    return `${currency?.symbol || '₹'}${amount.toFixed(2)}`;
  };

  const getEngagementBreakdown = (media, type) => {
    if (!media || media.length === 0) return null;

    return media.map(item => {
      if (type === 'youtube') {
        const likes = parseInt(item.statistics?.likeCount || item.likes || 0);
        const comments = parseInt(item.statistics?.commentCount || item.comments || 0);
        const favorites = parseInt(item.statistics?.favoriteCount || item.favorites || 0);
        const views = parseInt(item.statistics?.viewCount || item.views || 0);
        const engagements = likes + comments + favorites;
        
        return {
          title: item.title || item.snippet?.title || 'Untitled Video',
          engagements,
          reachs: views,
          likes,
          comments,
          favorites,
          shares: 0,
          saves: 0,
          // Add calculation breakdown if available
          engagementRevenue: calculationData?.campaign?.commission?.cpe ? engagements * calculationData.campaign.commission.cpe : 0,
          reachRevenue: calculationData?.campaign?.commission?.cpm ? (views / 1000) * calculationData.campaign.commission.cpm : 0
        };
      } else {
        const likes = parseInt(item.likes || item.statistics?.likeCount || 0);
        const comments = parseInt(item.comments || item.statistics?.commentCount || 0);
        const shares = parseInt(item.shares || item.statistics?.shares || 0);
        const saves = parseInt(item.saves || item.statistics?.saves || 0);
        const reachs = parseInt(item.reachs || item.statistics?.reachs || 0);
        const engagements = likes + comments + shares + saves;
        
        return {
          title: item.caption || item.title || 'Instagram Post',
          engagements,
          reachs,
          likes,
          comments,
          shares,
          saves,
          favorites: 0,
          // Add calculation breakdown if available
          engagementRevenue: calculationData?.campaign?.commission?.cpe ? engagements * calculationData.campaign.commission.cpe : 0,
          reachRevenue: calculationData?.campaign?.commission?.cpm ? (reachs / 1000) * calculationData.campaign.commission.cpm : 0
        };
      }
    });
  };

  // Helper to get platform revenue, preferring media-level revenue when available
  const getPlatformRevenue = (platformKey) => {
    if (!calculationData || !calculationData[platformKey]) return 0;

    const media = calculationData[platformKey].media || [];
    const mediaRevenue = media.reduce((sum, item) => sum + (item.revenue || 0), 0);
    const platformRevenue = calculationData[platformKey].revenue || 0;

    // If media revenue is present, use that (it matches totalDue in incremental cases)
    return mediaRevenue > 0 ? mediaRevenue : platformRevenue;
  };

  const getTotalRevenueDisplay = () => {
    if (!calculationData) return 0;

    if (typeof calculationData.total?.revenue === 'number') {
      return calculationData.total.revenue;
    }

    const youtubeRevenue = getPlatformRevenue('youtube');
    const instagramRevenue = getPlatformRevenue('instagram');
    const fixedAmount = calculationData.campaign?.commission?.amount || 0;
    const mediaTotal = youtubeRevenue + instagramRevenue + fixedAmount;

    if (mediaTotal > 0) return mediaTotal;
    if (calculationData.incrementalInfo?.totalDue > 0) return calculationData.incrementalInfo.totalDue;

    return 0;
  };

  if (initialLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-full space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        <p className="text-gray-600 font-medium">Loading payment details...</p>
      </div>
    );
  }

  if (autoCalculating) {
    return (
      <div className="flex flex-col items-center justify-center min-h-full space-y-4">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-orange-500"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-orange-500 text-lg font-bold">💰</div>
          </div>
        </div>
        <div className="text-center space-y-2">
          <p className="text-xl font-semibold text-gray-800">Calculating Payments...</p>
          <p className="text-gray-600">We're analyzing campaign performance and calculating earnings</p>
          <div className="flex items-center justify-center space-x-1 mt-4">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-full flex flex-col">
      <div className="flex-grow container mx-auto px-2 sm:px-6 py-6 sm:py-8">
        <ToastContainer position="top-right" autoClose={2000} />

        {/* Header Section - Mobile Responsive */}
        <div className="mb-6 space-y-4">
          {/* Back Button and Title - Same line for mobile */}
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 p-2 rounded-full hover:bg-gray-200 transition mr-3"
            >
              <FaArrowLeft size={20} className="sm:w-6 sm:h-6" />
            </button>
            <h2 className="text-sm md:text-lg lg:text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">
              Payment Details
            </h2>
          </div>

          {/* Action Buttons - Responsive Layout */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Recalculate Button - Full width on mobile */}
            <button
              onClick={calculatePayment}
              disabled={calculating}
              className="w-full sm:w-auto flex items-center justify-center px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 font-medium text-base sm:text-sm transition-colors"
            >
              <FaCalculator className="mr-2" />
              {calculating ? 'Recalculating...' : 'Re-calculate Payment'}
            </button>

            {/* Export and Print Buttons - Stack on mobile, inline on larger screens */}
            <div className="flex gap-3 sm:gap-4">
              <button
                onClick={handleExport}
                className="flex-1 sm:flex-none flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-base sm:text-sm transition-colors"
              >
                <FaDownload className="mr-2" /> Export
              </button>
              <button
                onClick={handlePrint}
                className="flex-1 sm:flex-none flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-base sm:text-sm transition-colors"
              >
                <FaPrint className="mr-2" /> Print
              </button>
            </div>
          </div>
        </div>
        {/* Affiliate Information Section */}
        <div className="bg-white rounded-xl shadow-sm p-2 sm:p-4 mb-6 sm:mb-8 border border-gray-100">
          <h2 className="text-sm md:text-lg lg:text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6">Affiliate Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-3 sm:space-y-4">
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Name</p>
                <p className="text-sm sm:text-base font-medium text-gray-900">
                  {affiliateInfo.firstName && affiliateInfo.lastName 
                    ? `${affiliateInfo.firstName} ${affiliateInfo.lastName}`
                    : affiliateInfo.username || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Email</p>
                <p className="text-sm sm:text-base font-medium text-gray-900">{affiliateInfo.email || 'N/A'}</p>
              </div>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Instagram ID</p>
                <p className="text-sm sm:text-base font-medium text-gray-900">{affiliateInfo.instagramId || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Affiliate Type</p>
                <p className="text-sm sm:text-base font-medium text-gray-900 capitalize">{affiliateInfo.affiliateType || 'N/A'}</p>
              </div>
              {affiliateInfo.companyName && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Company Name</p>
                  <p className="text-sm sm:text-base font-medium text-gray-900">{affiliateInfo.companyName}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Summary Section */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-100">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-800 mb-6">Payment Details</h2>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2">
              <span className="text-sm sm:text-base text-gray-600">Total Amount:</span>
              <span className="text-sm sm:text-base font-semibold text-gray-900">₹{(paymentData.totalDue || 0).toFixed(2)}</span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-sm sm:text-base text-gray-600">Paid Amount:</span>
              <span className="text-sm sm:text-base font-semibold text-gray-900">₹{(paymentData.amountPaid || 0).toFixed(2)}</span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-t border-gray-200 pt-4">
              <span className="text-sm sm:text-base font-semibold text-gray-800">Due Amount:</span>
              <span className="text-lg sm:text-xl font-bold text-orange-600">₹{(paymentData.amountUnpaid || 0).toFixed(2)}</span>
            </div>
            
            {!showPaymentForm && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={handlePayNow}
                  disabled={!paymentData.amountUnpaid || paymentData.amountUnpaid <= 0}
                  className="px-8 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-base transition-colors"
                >
                  Pay Now
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Payment Form Section - Only shown when Pay Now is clicked */}
        {showPaymentForm && (
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-100">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-800">Make Payment</h2>
              <button
                onClick={handleClosePaymentForm}
                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handlePaymentSubmit} className="space-y-6">
              <div>
                <div className="flex justify-between items-center py-2 mb-4">
                  <span className="text-sm sm:text-base text-gray-600">Remaining Due:</span>
                  <span className="text-sm sm:text-base font-semibold text-gray-900">₹{(paymentData.amountUnpaid || 0).toFixed(2)}</span>
                </div>
                
                <div>
                  <input
                    onWheel={(e) => e.target.blur()} 
                    type="number"
                    value={paymentAmount}
                    onChange={handlePaymentAmountChange}
                    placeholder="Enter Paid Amount"
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                    min="0"
                    step="0.01"
                    max={paymentData.amountUnpaid || 0}
                  />
                </div>
              </div>
              
              <div className="flex justify-center pt-4">
                <button
                  type="submit"
                  disabled={loading || !paymentAmount || paymentAmount <= 0}
                  className="px-8 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-base transition-colors"
                >
                  {loading ? 'Processing...' : 'Pay'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Calculation Details Section */}
        {calculationData && showCalculationDetails && (
            <div className="bg-white rounded-xl shadow-sm p-2 sm:p-4 mb-6 sm:mb-8 border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
              <h2 className="text-sm md:text-lg lg:text-xl sm:text-2xl font-semibold text-gray-800">Payment Calculation Details</h2>
              <div className="flex items-center space-x-3 sm:space-x-4">
                {calculationData.incrementalInfo && calculationData.incrementalInfo.isIncremental && (
                  <span className="text-xs sm:text-sm bg-green-100 text-green-800 px-2 sm:px-3 py-1 rounded-full">
                    ✓ Incremental Calculation
                  </span>
                )}
                <button
                  onClick={() => setShowRevenueDetails(!showRevenueDetails)}
                  className="text-orange-600 hover:text-orange-700 font-medium text-sm"
                >
                  {showRevenueDetails ? 'Hide Details' : 'Show Details'}
                </button>
              </div>
            </div>

            {showRevenueDetails && (
              <div>
                {/* Incremental Information Banner */}
            {calculationData.incrementalInfo && (
              <div className={`p-3 sm:p-4 rounded-lg mb-4 sm:mb-6 ${
                calculationData.incrementalInfo.isIncremental 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-start space-x-3">
                  <div className={`text-base sm:text-lg ${
                    calculationData.incrementalInfo.isIncremental ? 'text-green-600' : 'text-blue-600'
                  }`}>
                    {calculationData.incrementalInfo.isIncremental ? '📊' : '🆕'}
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-sm sm:text-base font-semibold mb-1 ${
                      calculationData.incrementalInfo.isIncremental ? 'text-green-800' : 'text-blue-800'
                    }`}>
                      {calculationData.incrementalInfo.isIncremental 
                        ? 'Incremental Revenue Calculation' 
                        : 'First-Time Revenue Calculation'}
                    </h3>
                    <p className={`text-xs sm:text-sm ${
                      calculationData.incrementalInfo.isIncremental ? 'text-green-700' : 'text-blue-700'
                    }`}>
                      {calculationData.incrementalInfo.isIncremental
                        ? `Only new engagement and reachs since the last payment of ₹${calculationData.incrementalInfo.lastPaidAmount.toFixed(2)} are included in this calculation.`
                        : 'This is the first payment calculation for this affiliate. All statistics are included.'
                      }
                    </p>
                    {calculationData.incrementalInfo.isIncremental && (
                      <div className="mt-2 text-xs text-green-600">
                        <div className="break-words">
                          Previous Unpaid: ₹{(calculationData.incrementalInfo.totalDue - calculationData.incrementalInfo.newRevenue).toFixed(2)} + 
                          New Revenue: ₹{calculationData.incrementalInfo.newRevenue.toFixed(2)} = 
                          Total Due: ₹{calculationData.incrementalInfo.totalDue.toFixed(2)}
                        </div>
                      </div>
                    )}
                    {!calculationData.incrementalInfo.isIncremental && (
                      <div className="mt-2 text-xs text-blue-600">
                        This calculation includes all approved content statistics.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div className="bg-orange-50 p-3 sm:p-4 rounded-lg">
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-orange-800 mb-2">Campaign Info</h3>
                <p className="text-xs sm:text-sm text-orange-600 mb-1">Name: {calculationData.campaign.name}</p>
                <p className="text-xs sm:text-sm text-orange-600 mb-1">Type: {calculationData.campaign.type}</p>
                <p className="text-xs sm:text-sm text-orange-600 mb-1">Model: {calculationData.campaign.commission.model}</p>
                {calculationData.campaign.commission.model === 'fixed' && (
                  <>
                    <p className="text-xs sm:text-sm text-orange-600 mb-1">CPE: {formatCurrency(calculationData.campaign.commission.cpe, calculationData.campaign.commission.currency)}</p>
                    <p className="text-xs sm:text-sm text-orange-600 mb-1">CPM: {formatCurrency(calculationData.campaign.commission.cpm, calculationData.campaign.commission.currency)}</p>
                    {calculationData.campaign.commission.amount && (
                      <p className="text-xs sm:text-sm text-orange-600">Fixed Amount: {formatCurrency(calculationData.campaign.commission.amount, calculationData.campaign.commission.currency)}</p>
                    )}
                  </>
                )}
              </div>

              <div className="bg-green-50 p-3 sm:p-4 rounded-lg">
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-green-800 mb-2">YouTube Revenue</h3>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600">
                  {formatCurrency(getPlatformRevenue('youtube'), calculationData.total.currency)}
                </p>
                <p className="text-xs sm:text-sm text-green-600">{calculationData.youtube.approvedVideos} approved videos</p>
                <p className="text-xs sm:text-sm text-green-600">{calculationData.youtube.totalVideos} total videos</p>
              </div>

              <div className="bg-purple-50 p-3 sm:p-4 rounded-lg sm:col-span-2 lg:col-span-1">
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-purple-800 mb-2">Instagram Revenue</h3>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-600">
                  {formatCurrency(getPlatformRevenue('instagram'), calculationData.total.currency)}
                </p>
                <p className="text-xs sm:text-sm text-purple-600">{calculationData.instagram.approvedPosts} approved posts</p>
                <p className="text-xs sm:text-sm text-purple-600">{calculationData.instagram.totalPosts} total posts</p>
              </div>
            </div>

            {/* Calculation Formulas */}
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6">
              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 mb-2">Calculation Formulas</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1">YouTube Formula:</h4>
                  <p className="text-gray-600 mb-1">Engagements = Likes + Comments + Favorites</p>
                  <p className="text-gray-600">Revenue = (Engagements × CPE) + (Reachs ÷ 1000 × CPM)</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1">Instagram Formula:</h4>
                  <p className="text-gray-600 mb-1">Engagements = Likes + Comments + Shares + Saves</p>
                  <p className="text-gray-600">Revenue = (Engagements × CPE) + (Reachs ÷ 1000 × CPM)</p>
                </div>
              </div>
            </div>

            {/* YouTube Media Breakdown */}
            {calculationData.youtube.media.length > 0 && (
              <div className="mb-4 sm:mb-6">
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                  <FaYoutube className="mr-2 text-red-600" />
                  YouTube Media Breakdown
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Video Title</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagements</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Reachs</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Likes</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Comments</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Favorites</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getEngagementBreakdown(calculationData.youtube.media, 'youtube')?.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 truncate max-w-xs" title={item.title}>
                            {item.title}
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 font-medium">{item.engagements.toLocaleString()}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">{item.reachs.toLocaleString()}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">{item.likes.toLocaleString()}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">{item.comments.toLocaleString()}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">{item.favorites.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Instagram Media Breakdown */}
            {calculationData.instagram.media.length > 0 && (
              <div className="mb-4 sm:mb-6">
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                  <FaInstagram className="mr-2 text-pink-600" />
                  Instagram Media Breakdown
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Post Caption</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagements</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Reachs</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Likes</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Comments</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Shares</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Saves</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getEngagementBreakdown(calculationData.instagram.media, 'instagram')?.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 truncate max-w-xs" title={item.title}>
                            {item.title}
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 font-medium">{item.engagements.toLocaleString()}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">{item.reachs.toLocaleString()}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">{item.likes.toLocaleString()}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">{item.comments.toLocaleString()}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">{item.shares.toLocaleString()}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">{item.saves.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Total Revenue Summary */}
            <div className="bg-orange-50 p-3 sm:p-4 rounded-lg">
              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-orange-800 mb-2">Total Revenue Summary</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <p className="text-xs sm:text-sm text-orange-600">YouTube Revenue</p>
                  <p className="text-sm sm:text-base lg:text-lg font-bold text-orange-800">
                    {formatCurrency(getPlatformRevenue('youtube'), calculationData.total.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-orange-600">Instagram Revenue</p>
                  <p className="text-sm sm:text-base lg:text-lg font-bold text-orange-800">
                    {formatCurrency(getPlatformRevenue('instagram'), calculationData.total.currency)}
                  </p>
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <p className="text-xs sm:text-sm text-orange-600">Total Due Amount</p>
                  <p className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-800">
                    {formatCurrency(getTotalRevenueDisplay(), calculationData.total.currency)}
                  </p>
                </div>
              </div>
              
              {/* Calculation Breakdown */}
              {calculationData.campaign.commission.model === 'fixed' && (
                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-orange-200">
                  <h4 className="text-xs sm:text-sm font-semibold text-orange-700 mb-2">Revenue Breakdown:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 text-xs">
                    <div>
                      <p className="text-orange-600">Engagement Revenue</p>
                      <p className="font-medium text-orange-800">
                        {formatCurrency(
                          ((getEngagementBreakdown(calculationData.youtube.media, 'youtube') || []).reduce(
                            (sum, item) => sum + (item.engagementRevenue || 0),
                            0
                          ) +
                            (getEngagementBreakdown(calculationData.instagram.media, 'instagram') || []).reduce(
                              (sum, item) => sum + (item.engagementRevenue || 0),
                              0
                            )),
                          calculationData.total.currency
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-orange-600">Reach Revenue</p>
                      <p className="font-medium text-orange-800">
                        {formatCurrency(
                          ((getEngagementBreakdown(calculationData.youtube.media, 'youtube') || []).reduce(
                            (sum, item) => sum + (item.reachRevenue || 0),
                            0
                          ) +
                            (getEngagementBreakdown(calculationData.instagram.media, 'instagram') || []).reduce(
                              (sum, item) => sum + (item.reachRevenue || 0),
                              0
                            )),
                          calculationData.total.currency
                        )}
                      </p>
                    </div>
                    <div className="sm:col-span-2 lg:col-span-1">
                      <p className="text-orange-600">Fixed Amount (once)</p>
                      <p className="font-medium text-orange-800">
                        {formatCurrency(
                          calculationData.campaign.commission.amount || 0,
                          calculationData.total.currency
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* No Media Data Message */}
            {calculationData.youtube.media.length === 0 && calculationData.instagram.media.length === 0 && (
              <div className="bg-yellow-50 p-3 sm:p-4 rounded-lg">
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-yellow-800 mb-2">No Approved Media Found</h3>
                <p className="text-xs sm:text-sm text-yellow-700">
                  No approved YouTube videos or Instagram posts were found for this affiliate and campaign. 
                  The affiliate needs to submit and get approval for their media content before payment can be calculated.
                </p>
              </div>
            )}

            {/* Zero Revenue Message */}
            {calculationData.total.revenue === 0 && (calculationData.youtube.media.length > 0 || calculationData.instagram.media.length > 0) && (
              <div className="bg-orange-50 p-3 sm:p-4 rounded-lg">
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-orange-800 mb-2">Zero Revenue Calculated</h3>
                <p className="text-xs sm:text-sm text-orange-700 mb-2">
                  Approved media was found but the calculated revenue is zero. This could be due to:
                </p>
                <ul className="text-xs sm:text-sm text-orange-700 list-disc list-inside space-y-1">
                  <li>Low engagement metrics on the approved media</li>
                  <li>Campaign commission model not configured for CPE/CPM calculation</li>
                  <li>Media statistics not available or zero</li>
                </ul>
              </div>
            )}
              </div>
            )}
          </div>
        )}

        {/* Payment History Section - Enhanced with statistics breakdown */}
        {paymentHistory.length > 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
              <h2 className="text-sm md:text-lg lg:text-xl sm:text-2xl font-semibold text-gray-800">Payment History & Statistics Tracking</h2>
              <div className="flex items-center space-x-2">
                <FaHistory className="text-gray-500" />
                <span className="text-xs sm:text-sm text-gray-500">Complete Payment History</span>
              </div>
            </div>

            <div className="space-y-4 sm:space-y-6">
              {paymentHistory.map((payment, index) => (
                <div key={index} className="border border-gray-200 rounded-xl p-3 sm:p-4 hover:bg-gray-50 transition-colors">
                  {/* Payment Summary */}
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 sm:mb-4 space-y-3 sm:space-y-0">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-2">
                        <span className="text-xs sm:text-sm text-gray-500">
                          {new Date(payment.updatedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        <span
                          className={`px-2 sm:px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full w-fit ${
                            payment.amountUnpaid === 0
                              ? 'bg-green-100 text-green-800'
                              : payment.amountPaid > 0
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {payment.amountUnpaid === 0 ? 'Fully Paid' : payment.amountPaid > 0 ? 'Partial Payment' : 'Unpaid'}
                        </span>
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        Updated by: {payment.lastUpdatedBy?.name || payment.brandName || 'System'}
                      </div>
                      {payment.referenceMessage && (
                        <div className="mt-2 text-xs sm:text-sm text-gray-700 bg-blue-50 border border-blue-200 rounded-md p-2">
                          <span className="font-semibold text-blue-900">Reference: </span>
                          <span className="text-blue-800">{payment.referenceMessage}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="text-base sm:text-lg font-semibold text-gray-900">
                        ₹{(payment.amountPaid || 0).toFixed(2)} paid
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500">
                        of ₹{(payment.totalDue || 0).toFixed(2)} due
                      </div>
                    </div>
                  </div>

                  {/* Revenue Breakdown */}
                  {payment.revenueBreakdown && (
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-800 mb-2">Revenue Breakdown</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-xs sm:text-sm">
                        <div>
                          <div className="text-gray-500">YouTube Revenue</div>
                          <div className="font-semibold text-red-600">
                            ₹{(payment.revenueBreakdown.youtubeRevenue || 0).toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Instagram Revenue</div>
                          <div className="font-semibold text-pink-600">
                            ₹{(payment.revenueBreakdown.instagramRevenue || 0).toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Total Revenue</div>
                          <div className="font-semibold text-orange-600">
                            ₹{(payment.revenueBreakdown.totalRevenue || 0).toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Incremental</div>
                          <div className="font-semibold text-green-600">
                            ₹{(payment.revenueBreakdown.incrementalRevenue || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Statistics at Payment Time */}
                  {payment.statsAtPayment && (
                    <div className="border-t pt-3 sm:pt-4">
                      <h4 className="text-xs sm:text-sm font-semibold text-gray-800 mb-2 sm:mb-3">Statistics at Payment Time</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {/* YouTube Stats */}
                        {payment.statsAtPayment.youtube && (
                          <div className="bg-red-50 rounded-lg p-2 sm:p-3">
                            <div className="flex items-center mb-2">
                              <FaYoutube className="text-red-600 mr-2" />
                              <span className="text-xs sm:text-sm font-semibold text-red-800">YouTube Statistics</span>
                            </div>
                            <div className="grid grid-cols-2 gap-1 sm:gap-2 text-xs">
                              <div>
                                <span className="text-gray-600">Videos:</span>
                                <span className="ml-1 font-medium">
                                  {payment.statsAtPayment.youtube.approvedVideos}/{payment.statsAtPayment.youtube.totalVideos}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Views:</span>
                                <span className="ml-1 font-medium">{(payment.statsAtPayment.youtube.totalViews || 0).toLocaleString()}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Likes:</span>
                                <span className="ml-1 font-medium">{(payment.statsAtPayment.youtube.totalLikes || 0).toLocaleString()}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Comments:</span>
                                <span className="ml-1 font-medium">{(payment.statsAtPayment.youtube.totalComments || 0).toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Instagram Stats */}
                        {payment.statsAtPayment.instagram && (
                          <div className="bg-pink-50 rounded-lg p-2 sm:p-3">
                            <div className="flex items-center mb-2">
                              <FaInstagram className="text-pink-600 mr-2" />
                              <span className="text-xs sm:text-sm font-semibold text-pink-800">Instagram Statistics</span>
                            </div>
                            <div className="grid grid-cols-2 gap-1 sm:gap-2 text-xs">
                              <div>
                                <span className="text-gray-600">Posts:</span>
                                <span className="ml-1 font-medium">
                                  {payment.statsAtPayment.instagram.approvedPosts}/{payment.statsAtPayment.instagram.totalPosts}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Reachs:</span>
                                <span className="ml-1 font-medium">{(payment.statsAtPayment.instagram.totalReachs || 0).toLocaleString()}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Likes:</span>
                                <span className="ml-1 font-medium">{(payment.statsAtPayment.instagram.totalLikes || 0).toLocaleString()}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Engagements:</span>
                                <span className="ml-1 font-medium">{(payment.statsAtPayment.instagram.totalEngagements || 0).toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Incremental Statistics */}
                      {payment.incrementalStats && (
                        <div className="mt-3 sm:mt-4 bg-green-50 rounded-lg p-2 sm:p-3">
                          <h5 className="text-xs sm:text-sm font-semibold text-green-800 mb-2">Incremental Statistics (Since Last Payment)</h5>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 text-xs">
                            <div>
                              <span className="text-gray-600">YT Views:</span>
                              <span className="ml-1 font-medium text-green-700">
                                +{(payment.incrementalStats.youtube?.totalViews || 0).toLocaleString()}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">YT Likes:</span>
                              <span className="ml-1 font-medium text-green-700">
                                +{(payment.incrementalStats.youtube?.totalLikes || 0).toLocaleString()}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">IG Reachs:</span>
                              <span className="ml-1 font-medium text-green-700">
                                +{(payment.incrementalStats.instagram?.totalReachs || 0).toLocaleString()}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">IG Engagements:</span>
                              <span className="ml-1 font-medium text-green-700">
                                +{(payment.incrementalStats.instagram?.totalEngagements || 0).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
              <h2 className="text-sm md:text-lg lg:text-xl sm:text-2xl font-semibold text-gray-800">Payment History & Statistics Tracking</h2>
              <div className="flex items-center space-x-2">
                <FaHistory className="text-gray-500" />
                <span className="text-xs sm:text-sm text-gray-500">Complete Payment History</span>
              </div>
            </div>
            <div className="text-center py-6 sm:py-8 text-gray-500">
              <div className="flex flex-col items-center">
                <div className="text-3xl sm:text-4xl mb-2">📊</div>
                <p className="text-base sm:text-lg font-medium text-gray-900 mb-1">No payment history yet</p>
                <p className="text-xs sm:text-sm text-gray-500">Payment history will appear here after the first payment is made.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliatePaymentDetails; 